<template>
  <div class="h-screen overflow-hidden flex flex-col justify-between bg-white border-solid border-l border-0 border-gray-border">
    <div class="flex-1 min-h-0 overflow-y-auto">
      <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
        <input-with-error-message v-if="isRenameMode"
                                  id="index-editor-name-input"
                                  :value.sync="indexName"
                                  :is-loading="isLoadingName"
                                  @escape="$emit('set-rename-mode', false)"
                                  @submit="$emit('rename',{ ...index, name: $event })"
                                  class="h-full mt-5 w-2/3"
        />
        <div v-else class="mt-8 inline-block pr-14 relative">
          <h3 class="font-semibold">
            {{ displayTitle }}
          </h3>
          <side-list-menu v-if="!isRenameMode"
                          :items="menuItems"
                          class="absolute top-0 right-0 -mt-1"
                          @rename="$emit('set-rename-mode', true)"
                          @reindex-all-sources="$emit('start-reindex', index)"
                          @reindex-all-markets="$emit('start-reindex-all-markets')"
                          @delete="$emit('request-delete', index)"
          />
        </div>
      </div>

      <div v-if="isIndexGroupPanel" class="mt-8 px-8 text-gray-dark">
        <label class="font-bold">Markets</label>
        <ul v-if="groupMarkets.length" class="mt-2 flex flex-col gap-2">
          <li v-for="market in groupMarkets"
              :key="market.marketId"
              class="text-gray-dark"
          >
            {{ market.name }}
          </li>
        </ul>
        <p v-else class="mt-2">No markets yet.</p>
      </div>
      <div v-else class="px-8">
        <base-toggle-group v-model="activePanel"
                           :tabs="editorTabs"
                           class="mt-6"
        />

        <template v-if="activePanel === 'scheduler'">
          <settings-index-scheduler :schedule-params="scheduleParams"
                                    @update-schedule-params="updateScheduleParams"
                                    class="mt-8"
          />
          <settings-index-provider v-if="showUnreleased && !isRenameMode"
                                   v-model="providerForm"
                                   class="mt-8"
          />
        </template>

        <knowledge-base-explorer v-else
                                 :index-id="index.id"
                                 class="mt-6 pb-8"
        />
      </div>
    </div>

    <div v-if="showFooterActions" class="grid grid-cols-2 gap-4 mb-10 px-8">
      <div class="col-span-1">
        <busy-button v-if="isIndexGroupPanel"
                     id="reindexAllMarketsButton"
                     variant="primary"
                     class="h-full w-full"
                     :is-loading="isLoadingReindex"
                     :is-disabled="isLoadingReindex"
                     @click="$emit('start-reindex-all-markets')"
        >
          Reindex all markets
        </busy-button>

        <busy-button v-else
                     id="saveButton"
                     :variant="saveButtonVariant"
                     class="h-full w-full"
                     :is-loading="isSavingIndex"
                     :is-disabled="isSavingIndex"
                     @click="saveIndex"
        >
          {{ saveButtonText }}
        </busy-button>
      </div>

      <div v-if="!isIndexGroupPanel" class="col-span-1">
        <busy-button id="reindexButton"
                     variant="bordered"
                     class="w-full"
                     :is-loading="isLoadingReindex"
                     :is-disabled="isLoadingReindex"
                     @click="$emit('start-reindex', index)"
        >
          Reindex now
        </busy-button>
      </div>
    </div>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import SettingsIndexScheduler from '@/components/settings/indexes/SettingsIndexScheduler.vue'
import SideListMenu from '@/components/common/SideListMenu'
import SettingsIndexProvider from '@/components/settings/indexes/SettingsIndexProvider.vue'
import KnowledgeBaseExplorer from '@/components/settings/KnowledgeBaseExplorer.vue'
import BaseToggleGroup from '@/components/base-components/BaseToggleGroup.vue'
import indexesService from '@/services/amio/indexes.service'
import toaster from '@/utils/toaster'
import mixins from '@/utils/mixins'
import { MENU_ITEMS, SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { SAVE_BUTTON_LABELS } from '@/utils/text.constants'
import { createDefaultProviderForm } from '@/utils/index-provider.utils'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'

const UPDATE_BUTTON_LABEL = 'Save'
const SUCCESS_BUTTON_LABEL = 'Saved!'

export default {
  name: 'SettingsIndexEditor',
  mixins: [mixins],
  components: {
    SettingsIndexProvider,
    BusyButton,
    InputWithErrorMessage,
    SideListMenu,
    SettingsIndexScheduler,
    KnowledgeBaseExplorer,
    BaseToggleGroup
  },
  props: {
    index: {
      type: Object,
      required: true
    },
    isLoadingName: {
      type: Boolean,
      default: false
    },
    isLoadingReindex: {
      type: Boolean,
      default: false
    },
    isRenameMode: {
      type: Boolean,
      default: false
    },
    headerTitle: {
      type: String,
      default: null
    },
    isIndexGroupPanel: {
      type: Boolean,
      default: false
    },
    groupMarkets: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    index: {
      immediate: true,
      handler(index) {
        this.indexName = this.headerTitle || index.name
        this.scheduleParams = index.schedule_params || null
        this.providerForm = index.provider === 'qdrant' ?
          createDefaultProviderForm(index.provider_params) :
          createDefaultProviderForm()
      }
    },
    headerTitle(headerTitle) {
      this.indexName = headerTitle || this.index?.name || ''
    }
  },
  data() {
    return {
      indexName: '',
      activePanel: 'scheduler',
      editorTabs: [
        { key: 'scheduler', label: 'Scheduler' },
        { key: 'explorer', label: 'Explorer' }
      ],
      scheduleParams: null,
      providerForm: createDefaultProviderForm(),
      saveButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      saveButtonText: UPDATE_BUTTON_LABEL,
      error: null,
      isSavingIndex: false,
      isEmpty
    }
  },
  computed: {
    displayTitle() {
      return this.headerTitle || this.index.name
    },
    menuItems() {
      if (this.isIndexGroupPanel) {
        return [MENU_ITEMS.rename, MENU_ITEMS.reindexAllMarkets, MENU_ITEMS.delete]
      }
      return [MENU_ITEMS.rename, MENU_ITEMS.reindexAllSources, MENU_ITEMS.delete]
    },
    showFooterActions() {
      return this.isIndexGroupPanel || this.activePanel !== 'explorer'
    }
  },
  methods: {
    updateScheduleParams(scheduleParams) {
      this.scheduleParams = scheduleParams
    },

    async saveIndex() {
      this.error = null
      this.isSavingIndex = true
      try {
        const payload = {
          schedule_params: this.scheduleParams,
          ...this.providerForm
        }
        await indexesService.update(this.index.id, payload)
        this.$emit('reload-indexes')
      } catch (err) {
        const data = err.response?.data
        const errorMessage = head(data?.details.errors)?.message || err.message
        this.error = errorMessage
        toaster.error(errorMessage)
      } finally {
        this.startButtonStateTimeout()
        this.isSavingIndex = false
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 3000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.saveButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      if (buttonState === 'DEFAULT') {
        this.saveButtonText = UPDATE_BUTTON_LABEL
      } else if (buttonState === 'SUCCESS') {
        this.saveButtonText = SUCCESS_BUTTON_LABEL
      } else {
        this.saveButtonText = SAVE_BUTTON_LABELS[buttonState]
      }
    }
  }
}
</script>
